/* The forager walking away from the camera: one stride in eight frames.
 *
 * WHAT WAS HERE BEFORE, and why it was replaced. The previous eight frames
 * were the two frames the game already shipped — `S.forager_up_0` and the
 * procedurally squashed `S.forager_up_1` from src/art.ts — each held for four
 * frames with a few pixels of scarf and boot jitter sprinkled on top. Measured:
 * frames 0,1,2,7 sat within 7-14 px of `S.forager_up_0`; frames 3,4,5,6 within
 * 12-24 px of `S.forager_up_1`; the two crossings between those clusters cost
 * 83 and 81 px. Two steps carried 71% of all the change in the cycle and four
 * steps changed under 9% of the ink. tools/spritecheck.ts passed it, because it
 * only asks that no two CONSECUTIVE frames be identical — which is exactly the
 * hole this file had fallen through.
 *
 * Two more faults came along for the ride. The low pose was a whole-body
 * translation, so the legs were swallowed by the hem for four frames running
 * and the actor appeared to sink into the floor for half the stride. And it
 * dipped once per stride rather than once per footfall, so a walk read as a
 * lurch at half the right frequency.
 *
 * HOW THIS ONE IS BUILT.
 *
 * The dip is a SQUASH, never a translation. The hood flattens on top, widens
 * through the middle, the shadow notch under the brim rides up a row and the
 * shoulders spread. The body loses a pixel of height without any part of it
 * being redrawn one row down, so the boots stay on the ground and the legs
 * stay visible in all eight frames.
 *
 * It squashes TWICE — frames 1-2 and 5-6 — because a stride has two footfalls.
 *
 * Everything that sways (hips, cloak hem, satchel, right cloak edge, scarf
 * tail) is anti-symmetric: its value at frame i+4 is the mirror of its value
 * at frame i. That is what stops eight frames from reading as a four-frame
 * loop played twice; the upper body is genuinely half a stride apart at 0 and
 * 4, not just the feet. Measured, the half-stride pairs d(i, i+4) are 25, 25,
 * 26 and 20 px apart.
 *
 * Those sway signals also change on precisely the four steps where the head
 * does NOT, so no step is a near-copy of its neighbour. Consecutive change is
 * 14, 23, 19, 14, 14, 21, 18, 19 px — a 1.64x spread, against 11.9x before.
 * Ink stays between 150 and 157 px: a walk is weight moving through a body,
 * not a body changing size.
 *
 * The beat, in order:
 *   0  CONTACT. Left boot lands; both soles flat, stance at its widest. Hips
 *      and cloak are at the right of their swing, scarf tail out to the right.
 *   1  DOWN. The body takes the weight: hood squashes a pixel into the
 *      shoulders, which spread. Right heel peels off, its sole shortens.
 *   2  PASS. Right leg airborne and swinging inside the left; stance narrowest.
 *      Satchel has dropped a row, hips crossing back through centre.
 *   3  UP. Right leg reaches out ahead, still off the floor. Body back to full
 *      height. Left heel is lifting behind it.
 *   4  CONTACT, mirrored. Right boot lands flat, left is up on the toe. Hips
 *      and cloak now at the left of their swing, scarf tail to the left.
 *   5  DOWN, mirrored. Second squash of the stride. Left foot leaves the floor.
 *   6  PASS, mirrored. Left leg airborne and swinging inside the right.
 *   7  UP, mirrored. Left leg reaches out — the pose that hands back into 0,
 *      where it lands. The loop closes on 19 changed pixels, mid-range.
 *
 * Frame 0 is `S.forager_up_0` with six pixels added — the hips at the right of
 * their swing, the cloak edge and the scarf tail out on that side. Hood, scarf,
 * belt, satchel, palette and silhouette are the shipped character untouched.
 */

export const forager_up_0 = [
  '................',
  '......oooo......',
  '.....oCCCCo.....',
  '...oCccccccCo...',
  '...oCcCCCCcCo...',
  '...oCcCCCCcCo...',
  '...oCccCCccCo...',
  '..oCkkkkkkkkCok.',
  '..oCccccccccCo..',
  '.ooCccccccccCo..',
  'oMoCccbbbbbbCo..',
  'oMoCccccccccCoo.',
  '..oCccccccccCoo.',
  '..ooccccccccooo.',
  '...obbo..obbo...',
  '...bbb....bbb...',
];

export const forager_up_1 = [
  '................',
  '.......oo.......',
  '....oCCCCCCo....',
  '...oCccccccCo...',
  '...oCcCCCCcCo...',
  '...oCccCCccCo...',
  '...oCccccccCo...',
  '.ooCkkkkkkkkCok.',
  '.ooCccccccccCoo.',
  '.ooCccccccccCo..',
  'oMoCccbbbbbbCo..',
  'oMoCccccccccCoo.',
  '..oCccccccccCoo.',
  '..ooccccccccooo.',
  '...obbo..obbo...',
  '...bbb....bb....',
];

export const forager_up_2 = [
  '................',
  '.......oo.......',
  '....oCCCCCCo....',
  '...oCccccccCo...',
  '...oCcCCCCcCo...',
  '...oCccCCccCo...',
  '...oCccccccCo...',
  '.ooCkkkkkkkkCoo.',
  '.ooCccccccccCoo.',
  '..oCccccccccCo..',
  '.ooCcbbbbbbcCo..',
  'oMoCccccccccCoo.',
  'oMoCccccccccCoo.',
  '..ooccccccccoo..',
  '..obbo.bbb......',
  '..bbb...........',
];

export const forager_up_3 = [
  '................',
  '......oooo......',
  '.....oCCCCo.....',
  '...oCccccccCo...',
  '...oCcCCCCcCo...',
  '...oCcCCCCcCo...',
  '...oCccCCccCo...',
  '..oCkkkkkkkkCo..',
  '..oCccccccccCo..',
  '..oCccccccccCo..',
  '.ooCcbbbbbbcCo..',
  'oMoCccccccccCoo.',
  'oMoCccccccccCoo.',
  '..ooccccccccoo..',
  '..obbo...bbb....',
  '..bb............',
];

export const forager_up_4 = [
  '................',
  '......oooo......',
  '.....oCCCCo.....',
  '...oCccccccCo...',
  '...oCcCCCCcCo...',
  '...oCcCCCCcCo...',
  '...oCccCCccCo...',
  '.koCkkkkkkkkCo..',
  '..oCccccccccCo..',
  '..oCccccccccCoo.',
  '.ooCbbbbbbccCoo.',
  'oMoCccccccccCo..',
  'oMoCccccccccCo..',
  '.oooccccccccoo..',
  '..obbo...obbo...',
  '..b.......bbb...',
];

export const forager_up_5 = [
  '................',
  '.......oo.......',
  '....oCCCCCCo....',
  '...oCccccccCo...',
  '...oCcCCCCcCo...',
  '...oCccCCccCo...',
  '...oCccccccCo...',
  '.koCkkkkkkkkCoo.',
  '.ooCccccccccCoo.',
  '..oCccccccccCoo.',
  '.ooCbbbbbbccCoo.',
  'oMoCccccccccCo..',
  'oMoCccccccccCo..',
  '.oooccccccccoo..',
  '..obbo...obbo...',
  '..........bbb...',
];

export const forager_up_6 = [
  '................',
  '.......oo.......',
  '....oCCCCCCo....',
  '...oCccccccCo...',
  '...oCcCCCCcCo...',
  '...oCccCCccCo...',
  '...oCccccccCo...',
  '.ooCkkkkkkkkCoo.',
  '.ooCccccccccCoo.',
  '.ooCccccccccCoo.',
  'oMoCcbbbbbbcCoo.',
  'oMoCccccccccCo..',
  '..oCccccccccCo..',
  '..ooccccccccoo..',
  '.....bbb..obbo..',
  '...........bbb..',
];

export const forager_up_7 = [
  '................',
  '......oooo......',
  '.....oCCCCo.....',
  '...oCccccccCo...',
  '...oCcCCCCcCo...',
  '...oCcCCCCcCo...',
  '...oCccCCccCo...',
  '..oCkkkkkkkkCo..',
  '..oCccccccccCo..',
  '.ooCccccccccCoo.',
  'oMoCcbbbbbbcCoo.',
  'oMoCccccccccCo..',
  '..oCccccccccCo..',
  '..ooccccccccoo..',
  '...bbb....obbo..',
  '...........bbb..',
];
